import { useToast } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { StepWizard, StepWizardSteps } from '@duke-hq/ui/components/StepWizard/StepWizard';
import { CourseSelection } from '@duke-hq/app-provider-web/tenant/Courses/CourseSelection';
import { CourseReview } from '@duke-hq/app-provider-web/tenant/Courses/CourseReview';
import { StudentSelection } from '@duke-hq/app-provider-web/tenant/students-page/StudentSelection';
import { EnrolmentSettings } from './EnrolmentSettings';
import { StudentWorkSchedule as WorkScheduleType } from '@duke-hq/svc-api-db';
import { CourseSegment, WorkSchedule } from '@duke-hq/shared-constants/enrolments';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { isDefined } from '@duke-hq/lib';
import { djs } from '@duke-hq/djs';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';

import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { CourseKindSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseKind.schema';
import { useTenantContext } from '@duke-hq/app-provider-web/tenant/TenantContext';
import { useAuthTenant } from '../useAuthTenant';
import { skipToken } from '@tanstack/react-query';
import { getCourseDatesFromAdminCourseSchedule, toUtcStartOfDay } from '@duke-hq/lib/dates';
interface IEnrolmentSettings {
  contract_category: string;
  start_at_date: string; // Must be in 'YYYY-MM-DD' format
  work_schedule: WorkScheduleType;
  enrolment_contract_type: string;
  course_segment: CourseSegment;
  site_location_id: string;
  consultant_id: string;
  external_id: string;
}

export interface EnrolmentSettingsData {
  course_id: string;
  user_id: string;
  enrolment_contract_type: string;
  start_at_date: string; // Must be in 'YYYY-MM-DD' format
  contract_category: string;
  work_schedule: WorkScheduleType;
  course_segment: string;
  user_timezone: string;
  consultant_id: string;
  site_location_id: string;
  external_id?: string;
}

export type StudentType = RouterOutputs['users']['studentById'];

export interface StudentOption extends StudentType {
  label?: string;
}

export function EnrolmentForm({
  student,
  referral,
  onClose,
}: {
  student?: StudentType | null | undefined;
  referral?: boolean;
  onClose?: () => void;
}) {
  const [studentSelected, setStudentSelected] = useState<StudentType | null | undefined>(student);
  const [course, setCourse] = useState<RouterOutputs['courses']['all'][0]>();
  const location = useLocation();
  const toast = useToast();
  const utils = trpcTenant.useUtils();
  const { studentId } = useParams<{ studentId: string }>();
  const createCourseEnrolment = trpcTenant.enrolments.createCourseEnrolment.useMutation();
  const tenantCtx = useTenantContext();
  const auth = useAuthTenant();

  const { data: courseSchedule, isLoading } = trpcTenant.courses.courseSchedule.useQuery(
    course?.id ?? skipToken,
    {
      enabled: course?.kind === CourseKindSchema.enum.SKILL_SET,
    },
  );

  const defaultValues = useMemo(
    () => ({
      external_id: '',
      start_at_date: djs().format('YYYY-MM-DD'),
      work_schedule: WorkSchedule.FULL_TIME,
      course_segment: CourseSegment.FULL_COURSE,
      ...(course?.kind === CourseKindSchema.enum.SKILL_SET
        ? {
            schedule: courseSchedule?.schedule,
          }
        : {
            enrolment_contract_type: '',
            contract_category: '',
            schedule: {} as Record<string, unknown>,
          }),
      site_location_id: '',
      consultant_id: tenantCtx?.permissions.includes('hasCaseload') ? auth.user.id : '',
    }),
    [course?.kind, auth.user.id, courseSchedule?.schedule, tenantCtx?.permissions],
  );

  const form = useForm<IEnrolmentSettings>({
    defaultValues: defaultValues,
  });

  const { trigger, control, reset } = form;

  useEffect(() => {
    reset(defaultValues);
  }, [
    course?.kind,
    reset,
    defaultValues,
    tenantCtx?.permissions,
    courseSchedule?.schedule,
    auth.user.id,
  ]);

  const [
    work_schedule,
    start_at_date,
    site_location_id,
    enrolment_contract_type,
    contract_category,
    course_segment,
    consultant_id,
    external_id,
  ] = useWatch({
    control,
    name: [
      'work_schedule',
      'start_at_date',
      'site_location_id',
      'enrolment_contract_type',
      'contract_category',
      'course_segment',
      'consultant_id',
      'external_id',
    ],
  });

  const courseStartAndEndDates = useMemo(() => {
    if (!courseSchedule || isLoading) {
      return null;
    }

    try {
      return getCourseDatesFromAdminCourseSchedule({
        adminCourseScheduleItems: courseSchedule.schedule,
        courseSegment: course_segment ?? CourseSegment.FULL_COURSE,
        workSchedule: work_schedule ?? WorkSchedule.FULL_TIME,
        startAt: toUtcStartOfDay(start_at_date, 'YYYY-MM-DD'),
      });
    } catch {
      // Handle bad user input gracefully
      return null;
    }
  }, [courseSchedule, course_segment, isLoading, start_at_date, work_schedule]);

  const handleSubmit = useCallback(async () => {
    const validated = await trigger();
    if (!validated) {
      return;
    }

    try {
      if (course && studentSelected) {
        const payload: EnrolmentSettingsData = {
          course_id: course.id,
          user_id: studentSelected.id,
          enrolment_contract_type: enrolment_contract_type,
          contract_category: contract_category,
          start_at_date: start_at_date,
          work_schedule: work_schedule,
          course_segment: course_segment,
          user_timezone: djs.tz.guess(),
          consultant_id: consultant_id,
          site_location_id: site_location_id,
          external_id: external_id,
        };

        const newEnrolment = await createCourseEnrolment.mutateAsync(payload);
        if (newEnrolment) {
          toast({
            title: 'Course enrolment created successfully.',
            status: 'success',
            duration: 7000,
            isClosable: true,
          });

          if (onClose) {
            onClose();
          }

          if (location.pathname.includes('/students')) {
            await utils.users.listStudentsWithPagination.refetch();
          }

          if (location.pathname.includes('/enrolments')) {
            await utils.enrolments.all.refetch();
            await utils.enrolments.enrolmentsReport.refetch();
          }

          if (studentId) {
            await utils.enrolments.getByUserId.refetch();
            await utils.users.listStudentsWithPagination.refetch();
          }
        }
      }
    } catch (error) {
      toast({
        title: t`An error occurred.`,
        description: error instanceof Error ? error.message : '',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    }
  }, [
    course,
    createCourseEnrolment,
    location.pathname,
    onClose,
    studentId,
    studentSelected,
    toast,
    utils.enrolments.all,
    utils.enrolments.getByUserId,
    utils.enrolments.enrolmentsReport,
    utils.users.listStudentsWithPagination,
    trigger,
    consultant_id,
    contract_category,
    course_segment,
    enrolment_contract_type,
    external_id,
    site_location_id,
    start_at_date,
    work_schedule,
  ]);

  const studentWithLabel = studentSelected
    ? {
        ...studentSelected,
        label: `${studentSelected.first_name} ${studentSelected.last_name}`,
      }
    : null;

  const steps = [
    {
      title: 'Select participant',
      page: (
        <StudentSelection
          student={studentWithLabel}
          onStudentSelected={setStudentSelected}
          disabledSelection={referral}
        />
      ),
      disabled: !studentWithLabel,
    },
    {
      title: 'Select course',
      page: studentWithLabel ? (
        <CourseSelection course={course} student={studentWithLabel} onCourse={setCourse} />
      ) : (
        <div>
          <Trans>No participant selected</Trans>
        </div>
      ),
      disabled: !course,
    },
    {
      title: 'Review',
      page: course ? (
        <CourseReview course={course} />
      ) : (
        <div>
          <Trans>No course selected</Trans>
        </div>
      ),
      disabled: !course,
    },
    {
      title: 'Enrolment settings',
      page:
        studentWithLabel && course ? (
          <EnrolmentSettings
            course={course}
            expectedCompletionDate={courseStartAndEndDates?.courseEndDate?.long}
          />
        ) : (
          <div>
            <Trans>No course or participant selected</Trans>
          </div>
        ),
      disabled: false,
    },
  ].filter(isDefined) as StepWizardSteps;

  return (
    <FormProvider {...form}>
      <StepWizard w="full" cursor="pointer" steps={steps} onSubmit={handleSubmit} />
    </FormProvider>
  );
}
